import './PrivacyPolicy.scss';

export default function PrivacyPolicy({ }) {

    return (
        <div className="PrivacyPolicy">
            <h1>Privacy Policy</h1>
            <p><strong>Effective Date:</strong> August 6, 2025</p>

            <section>
                <h2>1. Introduction</h2>
                <p>Welcome to <strong>Emetya.com</strong> (“Emetya”, “we”, “us”, or “our”). We value your privacy and are committed to protecting your personal data. This Privacy Policy outlines how we collect, use, share, and protect information when you visit our website and interact with our services, advertisements, or third-party offers.</p>
                <p>By using Emetya.com, you agree to the terms of this Privacy Policy. If you do not agree, please do not use our website.</p>
            </section>

            <section>
                <h2>2. Scope of This Privacy Policy</h2>
                <p>This Privacy Policy applies only to <strong>Emetya.com</strong>. It does <strong>not</strong> apply to third-party websites or telehealth providers that you may be redirected to through our affiliate links.</p>
            </section>

            <section>
                <h2>3. Information We Collect</h2>
                <h3>a) Personal Information You Provide:</h3>
                <ul>
                    <li>Name, email address, phone number (if submitted through forms)</li>
                    <li>Location (if shared via browser or IP)</li>
                    <li>Preferences or responses to surveys or quizzes</li>
                </ul>

                <h3>b) Automatically Collected Information:</h3>
                <ul>
                    <li>IP address and browser/device identifiers</li>
                    <li>Pages visited, time spent on the site, and click behavior</li>
                    <li>Referring URLs and exit pages</li>
                </ul>

                <h3>c) Cookies & Tracking Technologies:</h3>
                <p>We and our advertising partners (e.g., Google, Facebook, TikTok) use:</p>
                <ul>
                    <li>Cookies</li>
                    <li>Pixel tags</li>
                    <li>Local storage</li>
                    <li>Google Signals and Enhanced Conversions (if applicable)</li>
                </ul>
            </section>

            <section>
                <h2>4. How We Use Your Information</h2>
                <ul>
                    <li>To improve website functionality and performance</li>
                    <li>To personalize content and display relevant offers</li>
                    <li>To deliver targeted advertising through Google Ads, Meta Ads, and other platforms</li>
                    <li>To track conversions and optimize affiliate partnerships</li>
                    <li>To respond to inquiries and support requests</li>
                    <li>For legal compliance, analytics, and business operations</li>
                </ul>
            </section>

            <section>
                <h2>5. Google Ads Compliance</h2>
                <p>We comply with <strong>Google Ads’ data use policies</strong>, including:</p>
                <ul>
                    <li>Clear disclosure of data collection and sharing practices</li>
                    <li>No collection of sensitive personal data without consent</li>
                    <li>No combining of personally identifiable information (PII) with browsing behavior for remarketing without user opt-in</li>
                    <li>Providing user opt-out mechanisms such as:
                        <ul>
                            <li><a href="https://adssettings.google.com/" target="_blank" rel="noopener">Google Ads Settings</a></li>
                            <li><a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener">Network Advertising Initiative opt-out</a></li>
                        </ul>
                    </li>
                </ul>
            </section>

            <section>
                <h2>6. Do We Share Your Data?</h2>
                <p>We do <strong>not</strong> sell your personal information.</p>
                <p>We may share data with:</p>
                <ul>
                    <li>Affiliate partners and telehealth providers you engage with</li>
                    <li>Advertising networks for audience targeting</li>
                    <li>Analytics providers like Google Analytics and Hotjar</li>
                    <li>Service providers helping us operate the website</li>
                </ul>
            </section>

            <section>
                <h2>7. Healthcare & HIPAA-Related Disclosures</h2>
                <p>Emetya does <strong>not collect personal health data</strong> or offer medical services. We link to licensed <strong>third-party healthcare providers</strong> who are responsible for HIPAA compliance.</p>

                <h3>Patient Rights Through Those Providers May Include:</h3>
                <ul>
                    <li>Right to access your medical records</li>
                    <li>Right to request corrections</li>
                    <li>Right to receive a record of disclosures</li>
                    <li>Right to request communication preferences</li>
                    <li>Right to restrict disclosure of your data</li>
                    <li>Right to file a HIPAA complaint</li>
                    <li>Right to request a printed copy of the provider's privacy policy</li>
                </ul>
            </section>

            <section>
                <h2>8. Children’s Privacy</h2>
                <p>Our services are intended for adults 18 and over. We do not knowingly collect personal information from children. If we learn that we have inadvertently collected data from a minor, we will delete it promptly.</p>
            </section>

            <section>
                <h2>9. Data Security</h2>
                <p>We use industry-standard security measures and encryption to protect your information. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute protection.</p>
            </section>

            <section>
                <h2>10. Your Privacy Choices</h2>
                <p>You have the right to:</p>
                <ul>
                    <li>Opt out of personalized ads via <a href="https://adssettings.google.com/" target="_blank" rel="noopener">Google Ads Settings</a></li>
                    <li>Manage cookies via your browser</li>
                    <li>Request access, update, or deletion of your data (see Section 11)</li>
                    <li>Unsubscribe from marketing emails at any time</li>
                </ul>
            </section>

            <section>
                <h2>11. Your Rights Under U.S. Privacy Laws</h2>
                <p>Depending on your location, including California, you may have the right to:</p>
                <ul>
                    <li>Know what data we collect</li>
                    <li>Request deletion of your data</li>
                    <li>Opt out of data “sale” or “sharing”</li>
                    <li>Receive equal service regardless of your privacy choices</li>
                </ul>
                <p>To exercise these rights, please email: <a href="mailto:privacy@emetya.com">privacy@emetya.com</a></p>
            </section>

            <section>
                <h2>12. Third-Party Websites & Affiliate Offers</h2>
                <p>Our site includes affiliate links. If you click through and engage with a partner, we may earn a commission. These partners have their own privacy and HIPAA policies, which we encourage you to review before submitting personal information.</p>
            </section>

            <section>
                <h2>13. Updates to This Privacy Policy</h2>
                <p>This policy may be updated occasionally. Changes are effective immediately upon posting. The “Effective Date” at the top of the page reflects the latest version.</p>
            </section>

            <section>
                <h2>14. Contact Us</h2>
                <p>If you have any questions or privacy-related requests, contact us:</p>
                <p><strong>Email:</strong> <a href="mailto:privacy@emetya.com">privacy@emetya.com</a><br />
                    <strong>Mailing Address:</strong> Emetya, 18015 Collins St, Encino, CA 91316<br />
                    <strong>Website:</strong> <a href="https://www.emetya.com" target="_blank" rel="noopener">https://www.emetya.com</a>
                </p>
            </section>
        </div >
    )
}

